import { LoaderCircle } from 'lucide-react';
import { ElementRef, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import { ProductListGrid } from './components/ProductListGrid';

import { ProductCard } from '@/components/ProductCard';
import { SearchProductsFilter } from '@/components/SearchProductsFilter';
import { SearchProductsForm } from '@/components/SearchProductsForm';
import { withSearchProductsFilters } from '@/hocs/withSearchProductsFilter';
import { useGetProducts } from '@/hooks/useGetProducts';

export const VisionEnrichment = withSearchProductsFilters(
  ({ searchFilters, hasSearchFilters, handleSearchFilters }) => {
    const isFirstRender = useRef(true);
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const endProductsRef = useRef<ElementRef<'li'>>(null);

    const { t } = useTranslation('translation', {
      keyPrefix: 'pages.VisionEnrichment',
    });

    const {
      products,
      isFetching,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage,
    } = useGetProducts({ ...searchFilters, unique: true });

    useEffect(() => {
      if (
        isFirstRender.current &&
        !hasSearchFilters &&
        Object.keys(searchFilters).length === 0
      ) {
        handleSearchFilters('availableProduct', true);
      }

      isFirstRender.current = false;
    }, [hasSearchFilters, handleSearchFilters, searchFilters]);

    // Sync sessionStorage to URL when navigating to this page
    useEffect(() => {
      if (searchParams.size === 0 && Object.keys(searchFilters).length > 0) {
        const newParams = new URLSearchParams();
        Object.entries(searchFilters).forEach(([key, value]) => {
          if (value !== undefined) {
            newParams.set(key, String(value));
          }
        });
        if (newParams.size > 0) {
          setSearchParams(newParams, { replace: true });
        }
      }
    }, [location.pathname, searchParams.size, searchFilters, setSearchParams]);

    useEffect(() => {
      if (!endProductsRef.current) {
        return;
      }

      const observer = new IntersectionObserver(
        (entries, obs) => {
          const { isIntersecting } = entries[0];

          if (!hasNextPage) {
            obs.disconnect();
            return;
          }

          if (isIntersecting && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        {
          rootMargin: '30px',
        },
      );

      observer.observe(endProductsRef.current);

      return () => {
        observer.disconnect();
      };
    }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

    return (
      <div className="py-8">
        <section className="mb-10">
          <h1 className="text-[42px] tracking-[8.25px]">{t('title')}</h1>
        </section>

        <section>
          <SearchProductsForm
            searchFilters={searchFilters}
            onSearchFilters={handleSearchFilters}
          />

          <SearchProductsFilter
            searchFilters={searchFilters}
            onSearchFilters={handleSearchFilters}
          />

          <ProductListGrid>
            <>
              {products.map((product) => (
                <li key={product.id}>
                  <ProductCard product={product} />
                </li>
              ))}

              <li ref={endProductsRef}>
                {isFetching && (
                  <LoaderCircle className="mx-auto mt-10 animate-spin" />
                )}
              </li>
            </>
          </ProductListGrid>
        </section>
      </div>
    );
  },
  { persistFilters: true },
);
